import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Tabs as TabsPrimitive } from 'radix-ui'
import * as React from 'react'

type TabsVariant = 'default' | 'browser'

const TabsVariantContext = React.createContext<TabsVariant>('default')

const tabsRootVariants = cva('flex flex-col gap-2', {
  variants: {
    variant: {
      default: '',
      browser: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function Tabs({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  variant?: TabsVariant
}) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot='tabs'
        data-variant={variant}
        className={cn(tabsRootVariants({ variant, className }))}
        {...props}
      />
    </TabsVariantContext.Provider>
  )
}

const tabsListVariants = cva(
  'text-muted-foreground inline-flex items-center rounded-lg p-1',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        browser:
          'relative scrollbar-hide isolate items-center gap-0.5 rounded-none px-0 py-0 text-muted-foreground',
      },
      bg: {
        auto: '',
        secondary: 'bg-secondary border border-border',
        transparent: 'bg-transparent p-0 h-auto',
      },
      size: {
        default: 'h-9',
        sm: 'h-8',
        lg: 'h-10',
      },
      justify: {
        center: 'justify-center',
        start: 'justify-start',
      },
      width: {
        fit: 'w-fit',
        full: 'w-full min-w-0 overflow-x-auto overflow-y-hidden',
      },
    },
    compoundVariants: [
      {
        variant: 'browser',
        width: 'full',
        class: 'overflow-hidden',
      },
    ],
    defaultVariants: {
      variant: 'default',
      bg: 'auto',
      size: 'default',
      justify: 'center',
      width: 'fit',
    },
  }
)

function TabsList({
  className,
  variant: variantOverride,
  bg = 'auto',
  size = 'default',
  justify = 'center',
  width = 'fit',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  const contextVariant = React.useContext(TabsVariantContext)
  const variant = variantOverride ?? contextVariant

  return (
    <TabsPrimitive.List
      data-slot='tabs-list'
      data-variant={variant}
      data-size={size}
      data-justify={justify}
      data-width={width}
      className={cn(
        tabsListVariants({ variant, bg, size, justify, width, className })
      )}
      {...props}
    />
  )
}

const tabsTriggerVariants = cva(
  'inline-flex flex-1 cursor-pointer items-center rounded-md font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4 flex-none gap-1 rounded-[calc(var(--radius)-4px)] px-2 py-0.5 text-sm leading-5 text-muted-foreground data-[state=inactive]:bg-transparent data-[state=active]:bg-primary-foreground data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30',
        browser:
          'relative z-10 w-full min-w-0 max-w-none flex-auto items-center rounded-md border border-transparent bg-accent py-0 !h-8 gap-1 px-1.5 text-muted-foreground transition-none data-[state=active]:border-input data-[state=active]:bg-sidebar data-[state=active]:text-foreground data-[state=inactive]:hover:text-foreground',
      },
      size: {
        default: 'h-7 gap-1 px-2 py-1 text-sm',
        sm: 'h-7 gap-1 px-2 py-1 text-sm leading-5',
        lg: 'h-8 gap-1.5 px-3 py-1.5 text-sm',
      },
      justify: {
        center: 'justify-center',
        start: 'justify-start',
      },
      actionArea: {
        none: '',
        inlineEnd: 'pr-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      justify: 'center',
      actionArea: 'none',
    },
  }
)

function TabsTrigger({
  className,
  variant: variantOverride,
  size = 'default',
  justify = 'center',
  actionArea = 'none',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  const contextVariant = React.useContext(TabsVariantContext)
  const variant = variantOverride ?? contextVariant

  return (
    <TabsPrimitive.Trigger
      data-slot='tabs-trigger'
      data-variant={variant}
      data-size={size}
      data-justify={justify}
      data-action-area={actionArea}
      className={cn(
        tabsTriggerVariants({
          variant,
          size,
          justify,
          actionArea,
          className,
        })
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot='tabs-content'
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export {
  Tabs,
  TabsContent,
  TabsList,
  tabsListVariants,
  tabsRootVariants,
  TabsTrigger,
  tabsTriggerVariants,
}
